import { useTranslation } from 'react-i18next'
import { MapPin, Check, GripVertical, X, Info } from 'lucide-react'
import { SIGNER_COLORS } from '../../hooks/signerColors'
import type { PlacementTier } from '../../hooks/useSignaturePlacement'

interface SignatureFieldPlacerProps {
  email: string
  name: string
  colorIndex: number
  onPlace: () => void
  onRemove?: () => void
  isPlaced: boolean
  isPlacing: boolean
  disabled: boolean
  tier: PlacementTier
}

export function SignatureFieldPlacer({
  email,
  name,
  colorIndex,
  onPlace,
  onRemove,
  isPlaced,
  isPlacing,
  disabled,
  tier,
}: SignatureFieldPlacerProps) {
  const { t } = useTranslation()
  const colors = SIGNER_COLORS[colorIndex % SIGNER_COLORS.length]

  if (isPlaced) {
    const placedLabel = tier === 1
      ? t('signature.placedDrag')
      : t('signature.placedInline')

    return (
      <div className={`flex items-center gap-1.5 px-2 py-1 text-[10px] rounded border ${colors.bg} ${colors.border} ${colors.text}`}>
        {tier === 1 ? <GripVertical size={10} /> : <Check size={10} />}
        <span className="flex-1">{placedLabel}</span>
        {onRemove && (
          <button
            onClick={onRemove}
            className="hover:text-error-500 transition-colors"
            title={t('signature.removeField')}
          >
            <X size={10} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-0.5">
      {tier === 2 && !disabled && email && name && (
        <div className="flex items-center gap-1 text-[9px] text-neutral-400 dark:text-neutral-500">
          <Info size={8} className="shrink-0" />
          <span>{t('signature.placeHint')}</span>
        </div>
      )}
      <button
        onClick={onPlace}
        disabled={disabled || isPlacing || !email || !name}
        className={`flex items-center gap-1.5 px-2 py-1 text-[10px] rounded border transition-colors
          ${disabled || !email || !name
            ? 'border-neutral-200 dark:border-neutral-700 text-neutral-400 cursor-not-allowed'
            : `${colors.border} ${colors.text} hover:${colors.bg} cursor-pointer`
          }`}
        title={t('signature.placingField')}
      >
        <MapPin size={10} />
        <span>{isPlacing ? '...' : t('signature.placeField')}</span>
      </button>
    </div>
  )
}
